import * as React from "react";
import { format } from "date-fns";
import { ROLE_DISPLAY_NAMES } from "@repo/permissions";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Separator,
  ScrollArea,
  Alert,
  AlertDescription,
} from "@repo/ui";
import { Info, ChevronDown, ChevronUp } from "lucide-react";
import { ActionTypeBadge } from "./audit-log-columns";
import type { AuditLog } from "@/lib/types/inventory";

export interface AuditLogDetailModalProps {
  log: AuditLog | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ─── Friendly labels ────────────────────────────────────────────────────────

const tableNameLabels: Record<string, string> = {
  receipts: "Receipt",
  dispenses: "Dispense",
  inventory: "Inventory Item",
  users: "User",
  pharmacies: "Pharmacy Settings",
  stock_adjustments: "Stock Adjustment",
  inventory_audits: "Inventory Audit",
  audit_logs: "Audit Log",
  authorization_approvals: "Authorization",
};

/** Keys to always hide from old/new value display (technical / internal) */
const HIDDEN_KEYS = new Set([
  "id",
  "pharmacy_id",
  "user_id",
  "created_at",
  "updated_at",
  "auth_user_id",
  "is_active",
  "created_by",
  "updated_by",
  "password_hash",
  "must_change_password",
  // UUID references — not meaningful for end-users
  "approver_user_id",
  "requesting_user_id",
  "inventory_id",
  "wholesaler_id",
  "dispense_id",
  "receipt_id",
  "stock_adjustment_id",
  // Internal / deprecated fields
  "rx_number_hash",
  "context",
  "duplicate_acknowledged_by",
]);

/** Convert snake_case keys to human-readable labels */
function humanizeKey(key: string): string {
  const overrides: Record<string, string> = {
    // Inventory
    item_name: "Item Name",
    ndc: "NDC",
    drug_type: "Drug Schedule",
    stock_calculated: "Calculated Stock",
    stock_manual_override: "Manual Stock Override",
    stock_after: "Stock After",
    reorder_threshold: "Minimum Stock Level",
    // Receipts
    quantity_received: "Quantity Received",
    date_received: "Date Received",
    wholesaler: "Wholesaler",
    invoice_number: "Invoice Number",
    lot_number: "Lot Number",
    expiration_date: "Expiration Date",
    // Dispenses
    quantity_dispensed: "Quantity Dispensed",
    dispensed_at: "Dispensed At",
    rx_number: "Rx Number",
    is_duplicate_rx: "Duplicate Rx",
    date: "Date",
    // Authorization
    approver_name: "Approved By",
    approver_role: "Approver Role",
    requesting_user_name: "Requested By",
    permission: "Permission",
    // Users
    full_name: "Full Name",
    initials: "Initials",
    role: "Role",
    is_hidden: "Hidden",
    // Pharmacy settings
    legal_name: "Legal Name",
    dba_name: "DBA Name",
    ncpdp_id: "NCPDP ID",
    dea_registration_number: "DEA Number",
    dea_expiration_date: "DEA Expiration",
    state_license_number: "State License",
    license_expiration_date: "License Expiration",
    // Stock adjustments
    adjustment_type: "Adjustment Type",
    adjustment_reason: "Reason",
    justification: "Justification",
    expected_stock: "Expected Stock",
    actual_stock: "Actual Stock",
    password_confirmed: "Password Confirmed",
    notes: "Notes",
    ip_address: "IP Address",
  };
  if (overrides[key]) return overrides[key];
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Format a value for display */
function formatValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";

  const strVal = String(value);

  // Drug schedule formatting
  if (key === "drug_type") {
    const map: Record<string, string> = {
      schedule_ii: "Schedule II",
      schedule_iii: "Schedule III",
      schedule_iv: "Schedule IV",
      schedule_v: "Schedule V",
    };
    return map[strVal] || strVal;
  }

  // Role formatting
  if (key === "role" || key === "user_role") {
    return (
      ROLE_DISPLAY_NAMES[strVal as keyof typeof ROLE_DISPLAY_NAMES] || strVal
    );
  }

  // Adjustment type formatting
  if (key === "adjustment_type") {
    return strVal.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }

  // Permission formatting (e.g. "inventory:edit" → "Edit Inventory")
  if (key === "permission" && strVal.includes(":")) {
    const [resource, action] = strVal.split(":");
    const actionLabel = action.charAt(0).toUpperCase() + action.slice(1);
    const resourceLabel = resource.charAt(0).toUpperCase() + resource.slice(1);
    return `${actionLabel} ${resourceLabel}`;
  }

  // Date formatting
  if (
    key.includes("date") ||
    key.includes("_at") ||
    key === "expiration_date"
  ) {
    const d = new Date(strVal);
    if (!isNaN(d.getTime())) {
      // If it looks like a date-only string (YYYY-MM-DD), format without time
      if (/^\d{4}-\d{2}-\d{2}$/.test(strVal)) {
        return format(d, "MMM dd, yyyy");
      }
      return format(d, "MMM dd, yyyy 'at' h:mm a");
    }
  }

  return strVal;
}

/** Filter and prepare values for display — hides technical keys and empty values */
function prepareValues(
  values: Record<string, unknown> | null | undefined
): Array<{ key: string; label: string; value: string }> {
  if (!values) return [];
  return Object.entries(values)
    .filter(([key]) => !HIDDEN_KEYS.has(key))
    .map(([key, val]) => ({
      key,
      label: humanizeKey(key),
      value: formatValue(key, val),
    }))
    .filter((row) => row.value !== "—");
}

// ─── Component ──────────────────────────────────────────────────────────────

export function AuditLogDetailModal({
  log,
  open,
  onOpenChange,
}: AuditLogDetailModalProps) {
  const [showTechnical, setShowTechnical] = React.useState(false);

  if (!log) return null;

  const friendlyTable =
    tableNameLabels[log.table_name] || humanizeKey(log.table_name);
  const friendlyRole =
    ROLE_DISPLAY_NAMES[log.user_role as keyof typeof ROLE_DISPLAY_NAMES] ||
    log.user_role;

  const oldRows = prepareValues(log.old_values);
  const newRows = prepareValues(log.new_values);

  // Build a combined diff view when both old and new values exist
  const hasBothValues = oldRows.length > 0 && newRows.length > 0;
  const changedFields = hasBothValues
    ? (() => {
        const newMap = new Map(newRows.map((r) => [r.key, r.value]));
        const oldMap = new Map(oldRows.map((r) => [r.key, r.value]));
        const allKeys = new Set([
          ...oldRows.map((r) => r.key),
          ...newRows.map((r) => r.key),
        ]);
        const changes: Array<{
          label: string;
          oldVal: string;
          newVal: string;
        }> = [];
        for (const key of allKeys) {
          const ov = oldMap.get(key) ?? "—";
          const nv = newMap.get(key) ?? "—";
          if (ov !== nv) {
            changes.push({ label: humanizeKey(key), oldVal: ov, newVal: nv });
          }
        }
        return changes;
      })()
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Audit Log Details
          </DialogTitle>
          <DialogDescription>
            Complete audit trail for this action
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          <div className="space-y-5 pr-4">
            {/* Summary row */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <DetailField label="Action">
                <ActionTypeBadge actionType={log.action_type} />
              </DetailField>
              <DetailField label="Timestamp">
                <p className="text-sm font-medium">
                  {log.action_timestamp
                    ? format(
                        new Date(log.action_timestamp),
                        "MMM dd, yyyy 'at' h:mm a"
                      )
                    : "—"}
                </p>
              </DetailField>
              <DetailField label="Performed By">
                <p className="text-sm font-medium">{log.user_name}</p>
                <p className="text-xs text-muted-foreground">{friendlyRole}</p>
              </DetailField>
              <DetailField label="Affected Record">
                <p className="text-sm font-medium">{friendlyTable}</p>
              </DetailField>
            </div>

            {/* Justification */}
            {log.justification && (
              <>
                <Separator />
                <div className="space-y-1.5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Justification
                  </p>
                  <p className="text-sm bg-muted/50 px-3 py-2 rounded-lg">
                    {log.justification}
                  </p>
                </div>
              </>
            )}

            {/* Changed fields (diff view) */}
            {hasBothValues && changedFields.length > 0 && (
              <>
                <Separator />
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    What Changed
                  </p>
                  <div className="rounded-lg border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-table-header text-xs text-muted-foreground">
                          <th className="text-left px-3 py-2 font-medium">
                            Field
                          </th>
                          <th className="text-left px-3 py-2 font-medium">
                            Before
                          </th>
                          <th className="text-left px-3 py-2 font-medium">
                            After
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {changedFields.map((field) => (
                          <tr
                            key={field.label}
                            className="border-t border-border/60"
                          >
                            <td className="px-3 py-2 font-medium text-muted-foreground">
                              {field.label}
                            </td>
                            <td className="px-3 py-2 text-red-600 line-through decoration-red-300">
                              {field.oldVal}
                            </td>
                            <td className="px-3 py-2 text-emerald-600 font-medium">
                              {field.newVal}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {/* New values only (e.g. for "created" actions) */}
            {!hasBothValues && newRows.length > 0 && (
              <>
                <Separator />
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Values
                  </p>
                  <div className="rounded-lg border overflow-hidden">
                    <table className="w-full text-sm">
                      <tbody>
                        {newRows.map((row) => (
                          <tr
                            key={row.key}
                            className="border-t first:border-t-0 border-border/60"
                          >
                            <td className="px-3 py-2 font-medium text-muted-foreground w-1/3">
                              {row.label}
                            </td>
                            <td className="px-3 py-2">{row.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {/* Old values only (e.g. for "deleted" actions) */}
            {!hasBothValues && oldRows.length > 0 && (
              <>
                <Separator />
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Previous Values
                  </p>
                  <div className="rounded-lg border overflow-hidden">
                    <table className="w-full text-sm">
                      <tbody>
                        {oldRows.map((row) => (
                          <tr
                            key={row.key}
                            className="border-t first:border-t-0 border-border/60"
                          >
                            <td className="px-3 py-2 font-medium text-muted-foreground w-1/3">
                              {row.label}
                            </td>
                            <td className="px-3 py-2">{row.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {/* Technical details (collapsible) */}
            <Separator />
            <div>
              <button
                type="button"
                onClick={() => setShowTechnical(!showTechnical)}
                className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {showTechnical ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
                Technical Details
              </button>
              {showTechnical && (
                <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-2 text-xs animate-in fade-in-50 duration-200">
                  <div>
                    <p className="text-muted-foreground">Record ID</p>
                    <p className="font-mono">{log.record_id}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">User ID</p>
                    <p className="font-mono">{log.user_id}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Table</p>
                    <p className="font-mono">{log.table_name}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">IP Address</p>
                    <p className="font-mono">{log.ip_address}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Compliance note */}
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="text-xs">
                <strong>Compliance Note:</strong> This audit log entry is
                immutable and retained for a minimum of 7 years in accordance
                with DEA regulations.
              </AlertDescription>
            </Alert>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

// ─── Helper sub-component ───────────────────────────────────────────────────

function DetailField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-0.5">
        {label}
      </p>
      {children}
    </div>
  );
}
